import { useContext } from 'react'
import React, { createContext } from 'react'

export type User = {
    id: number
    email: string
    isMember: boolean
    isModerator: boolean
    blocked: boolean
    confirmed: boolean
    createdAt: string
    provider: 'local' | 'github' | 'google'
    username: string
    profile: { id: number; [key: string]: any }
    role: { type: 'authenticated' | 'public' | 'moderator' }
    wallet: { balance: number; transactions: any[] }
    [key: string]: any
}

type UserContextValue = {
    isLoading: boolean
    user: User | null
    isModerator: boolean
    setUser: React.Dispatch<React.SetStateAction<User | null>>
    fetchUser: (token?: string | null) => Promise<User | null>
    getJwt: () => Promise<string | null>
    login: (args: { email: string; password: string }) => Promise<User | null | { error: string }>
    logout: () => Promise<void>
    signUp: (args: {
        email: string
        password: string
        firstName: string
        lastName: string
    }) => Promise<User | null | { error: string }>
    isSubscribed: (contentType: 'topic' | 'question', id: number | string) => Promise<boolean>
    setSubscription: (args: {
        contentType: 'topic' | 'question'
        id: number | string
        subscribe: boolean
        user?: User
    }) => Promise<void>
    likePost: (id: number, unlike?: boolean, slug?: string) => Promise<void>
    likeRoadmap: (args: { id: number; unlike?: boolean; title?: string; user?: User }) => Promise<void>
    notifications: any
    setNotifications: any
    isValidating: boolean
    voteReply: (id: number, vote: 'up' | 'down', user?: User) => Promise<void>
    addBookmark: (args: { url: string; title: string; description: string }) => Promise<void>
    removeBookmark: (args: { url: string; title: string; description: string }) => Promise<void>
    reportSpam: (type: 'reply' | 'question', id: number) => Promise<void>
}

export const UserContext = createContext<UserContextValue>({
    isLoading: false,
    user: null,
    isModerator: false,
    setUser: () => {},
    fetchUser: async () => null,
    getJwt: async () => null,
    login: async () => null,
    logout: async () => {},
    signUp: async () => null,
    isSubscribed: async () => false,
    setSubscription: async () => undefined,
    likePost: async () => undefined,
    likeRoadmap: async () => undefined,
    notifications: [],
    setNotifications: () => undefined,
    isValidating: false,
    voteReply: async () => undefined,
    addBookmark: async () => undefined,
    removeBookmark: async () => undefined,
    reportSpam: async () => undefined,
})

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    return (
        <UserContext.Provider
            value={{
                isLoading: false,
                user: null,
                isModerator: false,
                setUser: () => {},
                fetchUser: async () => null,
                getJwt: async () => null,
                login: async () => null,
                logout: async () => {},
                signUp: async () => null,
                isSubscribed: async () => false,
                setSubscription: async () => undefined,
                likePost: async () => undefined,
                likeRoadmap: async () => undefined,
                notifications: [],
                setNotifications: () => undefined,
                isValidating: false,
                voteReply: async () => undefined,
                addBookmark: async () => undefined,
                removeBookmark: async () => undefined,
                reportSpam: async () => undefined,
            }}
        >
            {children}
        </UserContext.Provider>
    )
}

export const useUser = () => {
    return useContext(UserContext)
}
